import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useTranslation } from 'react-i18next';
import { ItemsFooterType } from '@celebration/react/ClbModal/ClbModal.types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ISupportModalProps from './interfaces/ISupportModalProps';
import { SULAHUB_ROLES } from '../../../../common/constants/roles';
import Auth from '../../../../app/contexts/auth';
import PolicyConstants from '../../../smartcargas/policy/constants';
import ConfirmationTermConstants from '../../../smartcargas/confirmation-term/constants';
import { supportItens } from './constants/support-itens';
import Icon from '../../../../common/components/icon';
import './support-modal.scss';

export default function SupportModal({
    isOpen,
    onClose,
}: Readonly<ISupportModalProps>) {
    const { t } = useTranslation();
    const { user } = Auth.useAuth();
    const navigate = useNavigate();

    const internalDocumentRoles = [
        SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
        SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.REQUESTER_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.INTEGRATION_USER,
    ];

    const externalDocumentRoles = [
        SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
        SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
        SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
    ];

    const filteredDocuments = useMemo(() => {
        if (!user?.roles) return [];

        return supportItens.filter(doc => {
            const allowedRoles = doc.isInternal
                ? internalDocumentRoles
                : externalDocumentRoles;

            return user.roles.some((userRole: string) =>
                allowedRoles.includes(userRole as any),
            );
        });
    }, [user?.roles]);

    const handleCardClick = (path: string) => {
        window.open(path, '_blank');
    };

    const handlePolicyClick = () => {
        onClose();
        navigate(`/${PolicyConstants.default}`);
    };

    const handleConfirmationTermClick = () => {
        onClose();
        navigate(`/${ConfirmationTermConstants.default}`);
    };

    const footerButtons: ItemsFooterType = [
        {
            key: 'close-button',
            id: 'close-button',
            label: t('Fechar'),
            disabled: false,
            loading: false,
            title: t('Fechar'),
            'data-testid': 'modal-close-button',
            onClick: onClose,
            role: 'button',
            size: 'md',
            styleType: 'primary',
        },
    ];

    return (
        <ClbModal
            title={t('Suporte')}
            open={isOpen}
            onClose={onClose}
            itemsFooter={footerButtons}
            data-testid="support-modal"
            width={900}
        >
            <div className="support-modal-content">
                {filteredDocuments.length > 0 ? (
                    <div className="document-cards-grid">
                        {filteredDocuments.map(doc => (
                            <button
                                key={doc.path}
                                type="button"
                                className="document-card"
                                onClick={() => handleCardClick(doc.path)}
                                data-testid={`pdf-card-${doc.name}`}
                            >
                                <div className="document-icon">
                                    <Icon size="lg" icon="file" />
                                </div>
                                <span className="document-name">
                                    {doc.name}
                                </span>
                            </button>
                        ))}
                        <button
                            type="button"
                            className="document-card"
                            onClick={handlePolicyClick}
                            data-testid="policy-card"
                        >
                            <div className="document-icon">
                                <Icon size="lg" icon="info" />
                            </div>
                            <span className="document-name">
                                {t('support.policy')}
                            </span>
                        </button>
                        <button
                            type="button"
                            className="document-card"
                            onClick={handleConfirmationTermClick}
                            data-testid="confirmation-term-card"
                        >
                            <div className="document-icon">
                                <Icon size="lg" icon="file" />
                            </div>
                            <span className="document-name">
                                {t('support.confirmationTerm')}
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className="no-documents">
                        <p>{t('support.noDocumentsAvailable')}</p>
                    </div>
                )}
            </div>
        </ClbModal>
    );
}
